/**
 * Lignes détaillées revenus / dépenses pour la fiche pocket (liste immobilière).
 * Les totaux affichés sur les cartes proviennent de l’analyse (property.* mergé).
 */
import { getSchlConfig } from '../defaults/schlBenchmarks.js';

function num(v) {
  return parseFloat(v) || 0;
}

function replacementReserveAmount(property) {
  const schlConfig = getSchlConfig({
    province: property.province,
    structureType: property.structureType || 'woodFrame',
    numberOfUnits: parseInt(property.numberOfUnits, 10) || 1,
    schlBenchmarkVersion: property.schlBenchmarkVersion,
  });
  const rrRates = schlConfig?.replacementReserve || {};
  return (
    (parseInt(property.numHeatPumps, 10) || 0) * (rrRates.heatPump || 0) +
    (parseInt(property.numFridges, 10) || 0) * (rrRates.appliance || 0) +
    (parseInt(property.numStoves, 10) || 0) * (rrRates.appliance || 0) +
    (parseInt(property.numDishwashers, 10) || 0) * (rrRates.appliance || 0) +
    (parseInt(property.numWashers, 10) || 0) * (rrRates.appliance || 0) +
    (parseInt(property.numDryers, 10) || 0) * (rrRates.appliance || 0) +
    (parseInt(property.numElevators, 10) || 0) * (rrRates.elevator || 0) * 12
  );
}

export function getPocketRevenueBreakdown(property) {
  const annualRent = num(property.annualRent);
  const parkingRevenue = num(property.parkingRevenue);
  const internetRevenue = num(property.internetRevenue);
  const storageRevenue = num(property.storageRevenue);
  const otherRevenue = num(property.otherRevenue);
  const totalGrossRevenue =
    annualRent + parkingRevenue + internetRevenue + storageRevenue + otherRevenue;
  const vacancyRate = num(property.vacancyRate) / 100;
  const vacancyAmount = totalGrossRevenue * vacancyRate;
  const effectiveGrossRevenue = totalGrossRevenue - vacancyAmount;

  const lines = [
    { key: 'annualRent', amount: annualRent },
    { key: 'parkingRevenue', amount: parkingRevenue },
    { key: 'internetRevenue', amount: internetRevenue },
    { key: 'storageRevenue', amount: storageRevenue },
    { key: 'otherRevenue', amount: otherRevenue },
  ].filter((row) => row.amount > 0);

  return {
    lines,
    totalGrossRevenue,
    vacancyAmount,
    effectiveGrossRevenue,
  };
}

export function getPocketExpenseBreakdown(property, advancedExpenses) {
  const numberOfUnits = parseInt(property.numberOfUnits, 10) || 1;
  const totalGrossRevenue =
    num(property.annualRent) +
    num(property.parkingRevenue) +
    num(property.internetRevenue) +
    num(property.storageRevenue) +
    num(property.otherRevenue);
  const vacancyAmount = totalGrossRevenue * (num(property.vacancyRate) / 100);
  const effectiveGrossRevenue = totalGrossRevenue - vacancyAmount;

  const lines = [];
  const push = (key, amount) => {
    if (amount > 0) lines.push({ key, amount });
  };

  if (advancedExpenses) {
    push('municipalTaxes', num(property.municipalTaxes));
    push('schoolTaxes', num(property.schoolTaxes));
    push('heating', num(property.heating));
    push('electricity', num(property.electricity));
    push('insurance', num(property.insurance));
    push('maintenancePerUnit', numberOfUnits * num(property.maintenance));
    push(
      'managementRate',
      effectiveGrossRevenue * (num(property.managementRate) / 100),
    );
    push('conciergePerUnit', numberOfUnits * num(property.concierge));
    push('landscaping', num(property.landscaping));
    push('snowRemoval', num(property.snowRemoval));
    push('extermination', num(property.extermination));
    push('fireInspection', num(property.fireInspection));
    push('advertising', num(property.advertising));
    push('legal', num(property.legal));
    push('accounting', num(property.accounting));
    push('cableInternet', num(property.cableInternet));
    push('appliances', num(property.appliances));
    push('garbage', num(property.garbage));
    push('washerDryer', num(property.washerDryer));
    push('hotWater', num(property.hotWater));
    push('otherExpenses', num(property.otherExpenses));
    const rr = replacementReserveAmount(property);
    if (rr > 0) push('replacementReserve', rr);
  } else {
    push('municipalTaxes', num(property.municipalTaxes));
    push('schoolTaxes', num(property.schoolTaxes));
    push('insurance', num(property.insurance));
    push('maintenancePerUnit', numberOfUnits * num(property.maintenance));
    push(
      'managementRate',
      effectiveGrossRevenue * (num(property.managementRate) / 100),
    );
    push('conciergePerUnit', numberOfUnits * num(property.concierge));
    push('electricityHeating', num(property.electricityHeating));
    push('otherExpenses', num(property.otherExpenses));
    const rr = replacementReserveAmount(property);
    if (rr > 0) push('replacementReserve', rr);
  }

  const totalFromAnalysis = num(property.totalExpenses);

  return { lines, totalFromAnalysis, vacancyAmount, advancedExpenses };
}
